import { DownOutlined } from '@ant-design/icons'
import { Collapse, Form, FormInstance } from 'antd'
import { useDiscountSchemas } from 'hooks/useDiscountSchemas'
import { DiscountSchema } from 'models/discount'
import { useState, useEffect } from 'react'
import { theme } from 'theme'
import { CreatePricingModalWrapper } from '../../../../atom/price'
import { PricingSchemaPayload } from '../../../../models/price'
import { Tenant } from '../../../../models/tenant'
import { DiscountFooterSectionModal } from './discount-footer-section'
import { InfoSectionModal } from './info-section'
import { NewDiscountSchemaSectionModal } from './new-discount-schema-section'
import React from 'react'
import { useGeneralTranslation } from 'hooks/translation/useGeneralTranslation'

interface props {
  tenant: Tenant
  selectedDiscountSchema: DiscountSchema
  selectedPricingSchema: PricingSchemaPayload | undefined
  discounts: DiscountSchema[]
  discountId: number
  onCancel: () => void
  onOk: () => void
  goBack?: () => void
  index?: number
  lastIndex?: number
  formRef?: (form: FormInstance) => void
  forms?: FormInstance[]
  setMultiDiscountNameExists?: React.Dispatch<React.SetStateAction<{}>>
  setMultiPromoCodeExists?: React.Dispatch<React.SetStateAction<{}>>
  handleSubmitAllForms?: () => Promise<void>
}

const RenderNameSection: React.FC<{
  children: React.ReactNode
  index?: number
  discountId: number
  activeCollapsePanelKey: string
  onCollapseChange: (key: string | string[]) => void
  renderHeader: () => React.ReactNode
}> = ({ children, index, discountId, activeCollapsePanelKey, onCollapseChange, renderHeader }) => {
  return index && index > 0 ? (
    <div style={{ marginTop: 24 }}>
      <Collapse
        accordion
        activeKey={activeCollapsePanelKey}
        className="custom-collapse"
        onChange={onCollapseChange}
        style={{ borderRadius: '5px', backgroundColor: theme.colors.white }}
      >
        <Collapse.Panel showArrow={false} header={renderHeader()} className="custom-panel" key={discountId}>
          {children}
        </Collapse.Panel>
      </Collapse>
    </div>
  ) : (
    <>{children}</>
  )
}

const EditDiscountSchemaSection: React.FC<props> = ({
  tenant,
  selectedDiscountSchema,
  selectedPricingSchema,
  discounts,
  discountId,
  onCancel,
  onOk,
  goBack,
  index,
  lastIndex,
  formRef,
  forms = [],
  setMultiDiscountNameExists,
  setMultiPromoCodeExists,
  handleSubmitAllForms,
}) => {
  const [form] = Form.useForm()
  const {
    formName,
    initialValues,
    discountNameExists,
    promoCodeExists,
    loiteringType,
    timeOfDay,
    eventSchedules,
    timeOfDayLoitering,
    basePricingPlanRate,
    basePricingPlanLoiteringRate,
    weekdayRates,
    weekendRates,
    weekdayLoiteringRates,
    weekendLoiteringRates,
    setLoiteringType,
    setTimeOfDay,
    handleSelect,
    setPricePerUnitLoitering,
    setTimeOfDayLoitering,
    convertRate,
    validateInputChange,
    getDiscountNames,
    nameCheck,
    onFinish,
    calculateDollarRateLimit,
  } = useDiscountSchemas(
    tenant,
    onOk,
    onCancel,
    selectedPricingSchema,
    discounts,
    selectedPricingSchema?.applied_listings,
    form,
    'edit',
    selectedDiscountSchema,
    index,
  )
  const [activeCollapsePanelKey, setActiveCollapsePanelKey] = useState<string>('')
  const { discountPlanText } = useGeneralTranslation()

  useEffect(() => {
    if (formRef) {
      formRef(form)
    }
  }, [form, formRef])

  const handleCollapseChange = (key: string | string[]) => {
    setActiveCollapsePanelKey((prevKey) =>
      prevKey === (Array.isArray(key) ? key[0] : key) ? '' : Array.isArray(key) ? key[0] : key,
    )
  }
  const renderCollapseHeader = () => {
    return (
      <div className="custom-panel-header body-01-regular">
        <div>{`${discountPlanText} ${index}`}</div>
        <span className={`custom-icon ${activeCollapsePanelKey === discountId.toString() ? 'rotate-icon' : ''}`}>
          <DownOutlined />
        </span>
      </div>
    )
  }

  useEffect(() => {
    if (setMultiDiscountNameExists && setMultiPromoCodeExists) {
      setMultiDiscountNameExists((prev) => ({
        ...prev,
        [discountId]: Object.values(discountNameExists).some((state) => state === true) || false,
      }))
      setMultiPromoCodeExists((prev) => ({
        ...prev,
        [discountId]: Object.values(promoCodeExists).some((state) => state === true) || false,
      }))
    }
  }, [discountNameExists, promoCodeExists, discountId, setMultiDiscountNameExists, setMultiPromoCodeExists])

  return (
    <CreatePricingModalWrapper key={discountId}>
      <Form
        colon={false}
        form={form}
        layout="horizontal"
        requiredMark={true}
        labelAlign="left"
        initialValues={initialValues}
        onValuesChange={() => {
          if (formRef) {
            formRef(form)
          }
        }}
        onFinish={(values) => {
          onFinish(values, 'editDiscountSchema', discountId)
        }}
        labelWrap={true}
      >
        {(index === 1 || index === undefined) && (
          <InfoSectionModal
            tenant={tenant}
            listings={selectedPricingSchema?.applied_listings}
            pricingSchemaValues={selectedPricingSchema}
          />
        )}
        <RenderNameSection
          index={index}
          discountId={discountId}
          activeCollapsePanelKey={activeCollapsePanelKey}
          onCollapseChange={handleCollapseChange}
          renderHeader={renderCollapseHeader}
        >
          <NewDiscountSchemaSectionModal
            initialValues={initialValues}
            tenant={tenant}
            form={form}
            formName={formName}
            discountNameExists={discountNameExists}
            promoCodeExists={promoCodeExists}
            pricingSchemaValues={selectedPricingSchema}
            timeOfDay={timeOfDay}
            eventSchedules={eventSchedules}
            timeOfDayLoitering={timeOfDayLoitering}
            loiteringType={loiteringType}
            modalType="edit"
            basePricingPlanRate={basePricingPlanRate}
            basePricingPlanLoiteringRate={basePricingPlanLoiteringRate}
            weekdayRates={weekdayRates}
            weekendRates={weekendRates}
            weekdayLoiteringRates={weekdayLoiteringRates}
            weekendLoiteringRates={weekendLoiteringRates}
            nameCheck={nameCheck}
            getDiscountNames={getDiscountNames}
            onCancel={onCancel}
            setTimeOfDay={setTimeOfDay}
            setLoiteringType={setLoiteringType}
            setPricePerUnitLoitering={setPricePerUnitLoitering}
            setTimeOfDayLoitering={setTimeOfDayLoitering}
            handleSelect={handleSelect}
            convertRate={convertRate}
            validateInputChange={validateInputChange}
            calculateDollarRateLimit={calculateDollarRateLimit}
            back={goBack}
            hideFooter={!!index}
            index={index}
          />
        </RenderNameSection>
        {!!index && !!lastIndex && lastIndex === 1 && (
          <DiscountFooterSectionModal
            loading={false}
            form={form}
            formName={formName}
            discountNameExists={discountNameExists}
            promoCodeExists={promoCodeExists}
            current={1}
            modalType={goBack ? 'discount-only' : 'edit'}
            onCancel={onCancel}
            back={goBack ? goBack : () => {}}
            forms={forms}
            handleSubmitAllForms={handleSubmitAllForms}
          />
        )}
      </Form>
    </CreatePricingModalWrapper>
  )
}

const arePropsEqual = (prevProps: props, nextProps: props) => {
  // Check if only setMultiDiscountNameExists or setMultiPromoCodeExists changed
  const setterFunctionsChanged =
    prevProps.setMultiDiscountNameExists !== nextProps.setMultiDiscountNameExists ||
    prevProps.setMultiPromoCodeExists !== nextProps.setMultiPromoCodeExists

  if (setterFunctionsChanged) {
    // If only setter functions changed, don't re-render
    return true
  }

  // For all other prop changes, do a deep comparison
  return JSON.stringify(prevProps) === JSON.stringify(nextProps)
}
export default React.memo(EditDiscountSchemaSection, arePropsEqual)
